import { useMemo } from 'react'
import {
    TIntegrationProvider,
    IntegrationsStore,
} from '@/core/store/Integrations/integrations.store'
import { useDrawer } from '@/core/context/DrawerContext'
import { integrationsModule } from '@/core/modules/integrations/integrations.module'
import { usePendingProviders } from '@/core/hooks/usePendingProviders/usePendingProviders'

export const useIntegrationProvider = (provider: TIntegrationProvider) => {
    const { integrationMode, type } = provider || {}
    const activeAndBrokenIntegrations =
        IntegrationsStore.getActiveAndBrokenIntegrationsByType(type)
    const { setDrawerState } = useDrawer()
    const { isGoogleFraudDefenseLoading, isMicrosoftFraudDefenseLoading } =
        usePendingProviders(provider)
    const msFraudDefenseProvider =
        IntegrationsStore.getProviderByType('ms_fraud_defense')
    const googleFraudDefenseProvider = IntegrationsStore.getProviderByType(
        'google_fraud_defense'
    )
    const crowdstrikeIntegration =
        IntegrationsStore.getIntegrationsByType('crowdstrike')[0] // crowdstrike is always single integration

    const shouldShowUpdateAccountBanner =
        type === 'microsoft' || type === 'google'

    const getFraudDefenseIntegrationData = () => {
        if (type === 'microsoft' || type === 'ms_fraud_defense') {
            return msFraudDefenseProvider
        }
        // google or google_fraud_defense
        else return googleFraudDefenseProvider
    }

    const handleAddAccount = async () => {
        if (type === 'crowdstrike') {
            setDrawerState((prevState: any) => ({
                ...prevState,
                isDrawerOpen: true,
                contentProps: {
                    integration: crowdstrikeIntegration,
                    onToggle: () => {},
                },
                drawerContentType: 'integration',
            }))
            return
        }

        const fdIntegration = getFraudDefenseIntegrationData()
        if (!fdIntegration) return

        integrationsModule.handleIntegrationLogin({
            providerId: fdIntegration.id,
            type: fdIntegration.type,
            origin: 'integrations-page',
            action: shouldShowUpdateAccountBanner ? 'update' : 'add',
        })
    }

    const sortedActiveAndBrokenIntegrations = useMemo(
        () =>
            activeAndBrokenIntegrations.sort((a, b) => {
                return (
                    new Date(a.activated_at || '').getTime() -
                    new Date(b.activated_at || '').getTime()
                )
            }),
        [activeAndBrokenIntegrations]
    )

    return {
        shouldShowAddAccount:
            integrationMode === 'multi' ||
            activeAndBrokenIntegrations.length === 0,
        shouldShowUpdateAccountBanner,
        isActionButtonLoading:
            (type === 'microsoft' && isMicrosoftFraudDefenseLoading) ||
            (type === 'google' && isGoogleFraudDefenseLoading),
        activeAndBrokenIntegrations: sortedActiveAndBrokenIntegrations,
        handleAddAccount,
    }
}
